import { constants } from "@qgiv/core-js";
import {
    Yup,
    VALIDATION_ERROR_MESSAGE,
    validateRequired,
    validateEmail,
    validatePhone,
    validateInternationalPhone,
    getAddress2Validations,
    evaluateIfFieldValueIsTrue,
    evaluateIfFieldValueIsFalse,
} from "@qgiv/core-validation";

// -------------------------------------------------------------------------
// MARK: Helper functions and variables
// -------------------------------------------------------------------------
const fundraisingGoalErrorMessage =
    "You’ll need to enter a goal in order to register.";
const waiverErrorMessage =
    "You’ll need to agree to the above statement in order to register.";
let isNonFundraisingParticipant;

// -------------------------------------------------------------------------
// MARK: Field Level Validations
// -------------------------------------------------------------------------
// Validation function for waiver
export const validateWaiver = (value) => {
    let error;
    if (value !== true) {
        // If no value is present
        error = waiverErrorMessage;
    }
    return error;
};

// Validation function for the Emergency_Contact_First_Name and the
// Emergency_Contact_First_Name_2 fields
export const validateEmergencyContactFirstName = (value) => {
    const hasError = validateRequired(value);
    let error;
    if (hasError) {
        // If no value is present
        error = VALIDATION_ERROR_MESSAGE.REQUIRED;
    }
    return error;
};

// Validation function for the Emergency_Contact_Last_Name and the
// Emergency_Contact_Last_Name_2 fields
export const validateEmergencyContactLastName = (value) => {
    const hasError = validateRequired(value);
    let error;
    if (hasError) {
        // If no value is present
        error = VALIDATION_ERROR_MESSAGE.REQUIRED;
    }
    return error;
};

// Validation function for the Emergency_Contact_Phone and the
// Emergency_Contact_Phone_2 fields
export const validateEmergencyContactPhone = (value) => {
    const hasRequiredError = validateRequired(value);
    const isValidPhone = validatePhone(value);
    let error;
    if (hasRequiredError) {
        // If no value is present
        error = VALIDATION_ERROR_MESSAGE.REQUIRED;
    } else if (!isValidPhone) {
        // If the value is not properly formatted
        error = VALIDATION_ERROR_MESSAGE.PHONE_FORMAT;
    }
    return error;
};

// Validation function for the Emergency_Contact_Cell and the
// Emergency_Contact_Cell_2 fields when required
export const validateRequiredEmergencyContactCell = (value) => {
    const hasRequiredError = validateRequired(value);
    const isValidPhone = validatePhone(value);
    let error;
    if (hasRequiredError) {
        // If no value is present
        error = VALIDATION_ERROR_MESSAGE.REQUIRED;
    } else if (!isValidPhone) {
        // If the value is not properly formatted
        error = VALIDATION_ERROR_MESSAGE.PHONE_FORMAT;
    }
    return error;
};

// Validation function for the Emergency_Contact_Cell and the
// Emergency_Contact_Cell_2 fields when optional
export const validateOptionalEmergencyContactCell = (value) => {
    const hasCellValue = Boolean(value);
    const isValidPhone = validatePhone(value);
    let error;
    if (hasCellValue) {
        if (!isValidPhone) {
            // If a value is present but the value is not properly formatted
            error = VALIDATION_ERROR_MESSAGE.PHONE_FORMAT;
        }
    }
    return error;
};

// Validation function for the Emergency_Contact_Email and the
// Emergency_Contact_Email_2 fields when required
export const validateRequiredEmergencyContactEmail = (value) => {
    const hasRequiredError = Boolean(validateRequired(value));
    const isValidEmail = validateEmail(value);
    let error;
    if (hasRequiredError) {
        // If no value is present
        error = VALIDATION_ERROR_MESSAGE.REQUIRED;
    } else if (!isValidEmail) {
        // If the value is not properly formatted
        error = VALIDATION_ERROR_MESSAGE.EMAIL_FORMAT;
    }
    return error;
};

// Validation function for the Emergency_Contact_Email and the
// Emergency_Contact_Email_2 fields when optional
export const validateOptionalEmergencyContactEmail = (value) => {
    const hasEmailValue = Boolean(value);
    const isValidEmail = validateEmail(value);
    let error;
    if (hasEmailValue) {
        if (!isValidEmail) {
            // If a value is present but the value is not properly formatted
            error = VALIDATION_ERROR_MESSAGE.EMAIL_FORMAT;
        }
    }
    return error;
};

// -------------------------------------------------------------------------
// MARK: Functions that generate individual schemas
// -------------------------------------------------------------------------
// Sets conditional validation requirements for a number of address fields
// based on if the user has not checked the Use Primary address field that is
// available to additional participants
const getRequireIfUsePrimaryAddressIsFalseSchema = () => {
    return Yup.string().when("Use_Primary_Address", {
        is: evaluateIfFieldValueIsTrue,
        then: Yup.string(),
        otherwise: Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED),
    });
};

// Sets conditional validation requirements for state and zip fields based on
// if the Use Primary address field that is available to additional
// participants is checked or if the participant is an international registrant
// as these fields do not always apply to international addresses
const getZipValidation = () => {
    return Yup.string().when(["Use_Primary_Address", "Country"], {
        is: (usePrimaryAddress, country) => {
            if (usePrimaryAddress === true || country !== "US") {
                return true;
            }
            return false;
        },
        then: Yup.string(),
        otherwise: Yup.string()
            .required(VALIDATION_ERROR_MESSAGE.REQUIRED)
            .matches(/^[0-9]+$/, VALIDATION_ERROR_MESSAGE.ZIP)
            .min(5, VALIDATION_ERROR_MESSAGE.ZIP)
            .max(5, VALIDATION_ERROR_MESSAGE.ZIP),
    });
};

// Sets conditional validation requirements for state and zip fields based on
// if the Use Primary address field that is available to additional
// participants is checked or if the participant is an international registrant
// as these fields do not always apply to international addresses
const getStateValidation = () => {
    return Yup.string().when(["Use_Primary_Address", "Country"], {
        is: (usePrimaryAddress, country) => {
            if (usePrimaryAddress === true || country !== "US") {
                return true;
            }
            return false;
        },
        then: Yup.string(),
        otherwise: Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED),
    });
};

// Set phone validation requirements based on phoneFieldsType and the value
// whether or not the use my phone field has been checked by additional
// participants
const getPhoneValidation = (phoneFieldsType, isChildParticipant) => {
    const {
        ENUMS: {
            Status: { REQUIRED },
        },
    } = constants;

    if (phoneFieldsType === REQUIRED) {
        return {
            Phone: Yup.string().test(
                "phone test",
                "Please enter a valid phone number.",
                (phoneValue, context) => {
                    const currentObjectValues = context?.from[0].value;
                    const { Use_Primary_Phone, Country } = currentObjectValues;
                    // bypass validation if using primary's phone number
                    if (isChildParticipant && Use_Primary_Phone) {
                        return true;
                    }
                    if (Country === "US") {
                        return validatePhone(phoneValue);
                    }
                    // international phone number validation
                    return validateInternationalPhone(phoneValue);
                },
            ),
        };
    }
    // Phone number is not required
    // A value for Phone Number still needs to be in the correct shape
    // if the address is from the US
    return {
        Phone: Yup.string().when("Country", {
            is: (Country) => Country === "US",
            then: Yup.string().validatePhoneString(),
            otherwise: Yup.string().validateInternationalPhoneString(),
        }),
    };
};

// Sets conditional validation requirements for the email address field
// based on if the user has checked Is_Nfp or Managed by Primary field or
// has selected a category the Is Nfp Only. Both do not require an email
// and therefore will never trigger an error
const getVerifiedEmailValidation = () => {
    return Yup.boolean().when(["Is_Nfp", "Managed_By_Primary"], {
        is: (isNfp, managedByPrimary) => {
            if (isNfp || managedByPrimary) {
                return true;
            }
            return false;
        },
        then: Yup.boolean(),
        otherwise: Yup.boolean().oneOf([true]),
    });
};

// -------------------------------------------------------------------------
// MARK: Form level validation schema
// -------------------------------------------------------------------------
const getRegistrationPageValidations = (validationSettings) => {
    const {
        ENUMS: {
            EntityType: { TEAM },
        },
    } = constants;
    const {
        commitmentDetails,
        currency,
        currencyString,
        eventEnableCommitments,
        isChildParticipant,
        minParticipantGoal,
        minTeamGoal,
        phoneFieldsType,
        categories,
    } = validationSettings;
    // Generate individual validation schemas
    const citySchema = getRequireIfUsePrimaryAddressIsFalseSchema();
    const stateSchema = getStateValidation();
    const zipSchema = getZipValidation();
    const countrySchema = getRequireIfUsePrimaryAddressIsFalseSchema();
    // re assignable variables for use in fundraising goal validation
    let goal;
    let currentCategory;
    let participantFundraisingGoalMessage;
    let hasCommitmentOrMinParticipantGoal;
    let hasCommitmentAmount;
    let hasMinParticipantGoal;
    let selectedCategory;
    // Generate combined form level validation schema
    const registrationPageSchema = {
        ...getAddress2Validations(),
        ...getPhoneValidation(phoneFieldsType, isChildParticipant),
        First_Name: Yup.string()
            .required(VALIDATION_ERROR_MESSAGE.REQUIRED)
            .validateIsNotACreditCardNumberString(),
        Last_Name: Yup.string()
            .required(VALIDATION_ERROR_MESSAGE.REQUIRED)
            .validateIsNotACreditCardNumberString(),
        // Use my phone field for additional registrants
        Use_Primary_Phone: Yup.bool().required(),
        // Require unless the user is a non-fundraising additional participant
        Email: Yup.string().when(["Is_Nfp", "Managed_By_Primary"], {
            is: (isNfp, managedByPrimary) => {
                return isNfp ? isNfp && isChildParticipant : managedByPrimary;
            },
            then: Yup.string().email(VALIDATION_ERROR_MESSAGE.EMAIL_FORMAT),
            otherwise: Yup.string()
                .required(VALIDATION_ERROR_MESSAGE.REQUIRED)
                .email(VALIDATION_ERROR_MESSAGE.EMAIL_FORMAT),
        }),
        Address_1: Yup.string().when("Use_Primary_Address", {
            is: evaluateIfFieldValueIsTrue,
            then: Yup.string().validateIsNotACreditCardNumberString(),
            otherwise: Yup.string()
                .required(VALIDATION_ERROR_MESSAGE.REQUIRED)
                .validateIsNotACreditCardNumberString(),
        }),
        City: citySchema,
        State: stateSchema,
        Zip: zipSchema,
        Country: countrySchema,
        // Use my address field for additional registrants
        Use_Primary_Address: Yup.bool().required(),
        // Is the participant a non-fundraising participant
        Is_Nfp: Yup.bool().required(),
        // Is this participant's fundraising page being managed by the primary registrant
        Managed_By_Primary: Yup.bool().required(),
        // Fundraising Goal
        Participant_Fundraising_Goal: Yup.string().when(
            ["Is_Nfp", "Category"],
            {
                is: (isNfp, Category) => {
                    // sets globally scoped helper variable for use in "otherwise" eval.
                    isNonFundraisingParticipant = isNfp;
                    currentCategory = Category;
                    selectedCategory = categories.find(
                        (category) => category.id === Number(Category),
                    ) || { enableCommitments: false };
                    const categoryEnableCommitments =
                        !!selectedCategory?.enableCommitments;

                    hasCommitmentOrMinParticipantGoal =
                        (eventEnableCommitments &&
                            categoryEnableCommitments &&
                            Number(commitmentDetails[Category]?.amount) > 0) ||
                        Number(minParticipantGoal) > 0;
                    hasCommitmentAmount =
                        eventEnableCommitments &&
                        categoryEnableCommitments &&
                        Number(commitmentDetails[Category]?.amount) > 0;
                    hasMinParticipantGoal =
                        (Number(minParticipantGoal) > 0 &&
                            Number(commitmentDetails[Category]?.amount) === 0 &&
                            categoryEnableCommitments) ||
                        (Number(minParticipantGoal) > 0 &&
                            !categoryEnableCommitments) ||
                        (Number(minParticipantGoal) > 0 &&
                            !eventEnableCommitments);

                    if (
                        evaluateIfFieldValueIsFalse(isNfp) &&
                        hasCommitmentOrMinParticipantGoal
                    ) {
                        goal = hasCommitmentAmount
                            ? commitmentDetails[Category]?.amount
                            : hasMinParticipantGoal && minParticipantGoal;
                        participantFundraisingGoalMessage = `You must set a goal of at least ${currencyString(
                            goal,
                            currency,
                        )}`;
                        return true;
                    }
                    return false;
                },
                then: () => {
                    return hasMinParticipantGoal
                        ? Yup.string()
                              .required(participantFundraisingGoalMessage)
                              .validateParticipantFundraisingGoalString(
                                  goal,
                                  participantFundraisingGoalMessage,
                              )
                        : Yup.string()
                              .required(participantFundraisingGoalMessage)
                              .validateCategoryCommitmentAmount(
                                  commitmentDetails[currentCategory]?.amount,
                                  participantFundraisingGoalMessage,
                              );
                },
                otherwise: () =>
                    isNonFundraisingParticipant
                        ? Yup.string()
                        : Yup.string().required(fundraisingGoalErrorMessage),
            },
        ),
        // A user can only select one category
        Category: Yup.array().min(1).max(1).required(),
        // A user can only have one registration role
        Registration_Role: Yup.array().min(1).max(1).required(),
        // The user is only required to enter a team value when the
        // registration role has been set to team
        Team: Yup.string().when("Registration_Role", {
            is: (registrationRole) => {
                const hasSelectedRegistrationRole = registrationRole.length > 0;
                if (hasSelectedRegistrationRole) {
                    const hasSelectedTeamRegistrationRole =
                        registrationRole[0] === TEAM;
                    if (hasSelectedTeamRegistrationRole) {
                        return true;
                    }
                }
                return false;
            },
            then: Yup.string().required(),
            otherwise: Yup.string(),
        }),
        Team_Captain: Yup.bool().required(),
        New_Team_Name: Yup.string(),
        // Only check to see if the user has entered a team fundraising goal
        // that is greater than the minimum team goal if they have entered a
        //  team name and have started entering a fundraising goal value
        New_Team_Fundraising_Goal: Yup.string().when("New_Team_Name", {
            is: (newTeamName) => {
                const hasCreatedNewTeam = Boolean(newTeamName);
                if (hasCreatedNewTeam) {
                    return true;
                }
                return false;
            },
            then: () => {
                const message = `You must set a goal of at least ${currencyString(
                    minTeamGoal,
                    currency,
                )}`;
                return Yup.string()
                    .required(VALIDATION_ERROR_MESSAGE.REQUIRED)
                    .validateNewTeamFundraisingGoalString(minTeamGoal, message);
            },
            otherwise: Yup.string(),
        }),
    };

    return registrationPageSchema;
};

export const getValidationSchema = (validationSettings) => {
    const validationShape = {
        ...getRegistrationPageValidations(validationSettings),
    };
    const schema = Yup.object().shape(validationShape);

    return schema;
};
